import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ApiCall from "../../utils/apicall";
import { fDate } from "../../utils/formatTime";
import {
  Card,
  ListItem,
  ListItemText,
  Stack,
  Paper,
  Typography,
  Button,
  Grid,
  Breadcrumbs,
  Container,
  TableContainer,
  TableCell,
  Table,
  TableRow,
  TableBody,
  TableHead,
} from "@mui/material";
import { HomeRounded, PrintRounded } from "@mui/icons-material";

// Utility function to format phone number as (123) 456-7890
function formatPhoneNumber(phoneNumberString) {
  var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    var intlCode = "+1 ";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }
  return null;
}
function formatfaxNumber(faxNumberString) {
  var cleaned = ("" + faxNumberString).replace(/\D/g, "");
  var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return ["+1", "-", match[2], "-", match[3], "-", match[4]].join("");
  }
  return null;
}

// Currency formatter for print layout totals.
function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

// CR-2026-FE-PO-AISC-TERMS-BLOCK Option A — structural-fab keyword heuristic.
// Returns true if any line-item description, notes, or term field suggests
// the PO covers structural-steel fabrication (in which case the AISC
// 9-category clause block is rendered on the print layout). Conservative
// keyword list — false-positives are harmless (extra terms on a non-fab PO);
// false-negatives risk missing AISC terms on a fab PO so the list is broad.
const STRUCTURAL_FAB_KEYWORDS = [
  "beam", "column", "plate", "tube", "hss", "bolt", "weld", "steel",
  "angle", "channel", "gusset", "connection", "anchor", "stud", "rebar",
  "girder", "joist", "purlin", "truss", "moment frame", "shear",
  "structural", "fabricat", "erect",
];

function isStructuralFabPo(formData, items) {
  if (!formData) return false;
  const haystack = [
    formData.notes || "",
    formData.term || "",
    ...(items || []).map(
      (it) =>
        (it.Inventory && it.Inventory.ediStdNomenclature) ||
        it.description ||
        ""
    ),
  ].join(" ").toLowerCase();
  return STRUCTURAL_FAB_KEYWORDS.some((kw) => haystack.includes(kw));
}

const TABLE_HEAD = [
  { id: "address", label: "Address" },
  { id: "delivery_date", label: "Delivery Date" },
  { id: "confirm_with", label: "Confirm With" },
  { id: "phone", label: "Phone" },
  { id: "order_date", label: "Order Date" },
  { id: "placed_via", label: "Placed Via" },
  { id: "po_number", label: "PO Number" },
  { id: "ship_via", label: "Ship Via" },
  { id: "order_by", label: "Order By" },
  { id: "ship_to", label: "Ship To" },
  { id: "email", label: "Email" },
  { id: "term", label: "Term" },
  { id: "fax", label: "Fax" },
];

// CR-2026-FE-PO-PRINT-LAYOUT (Class P) — print-only stylesheet that emits an
// A4-formatted PO cover sheet on Cmd+P / Ctrl+P. Pattern: hide everything on
// the page with visibility:hidden, then re-show only the .po-print-view tree
// at absolute position. References D-034 §3.1 source-of-design in the
// footer for AISC clause traceability — see
// sfm-drawing-feature/docs/qms/SFM_Reference_Forms_PO_Templates.md.
const PRINT_CSS = `
@media print {
  body * { visibility: hidden; }
  .po-print-view, .po-print-view * { visibility: visible; }
  .po-print-view {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    padding: 12mm 14mm;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 10pt;
    color: #000;
    background: #fff;
  }
  .po-screen-view { display: none !important; }
  .po-print-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border-bottom: 2px solid #000;
    padding-bottom: 6mm;
    margin-bottom: 6mm;
  }
  .po-print-title {
    font-size: 22pt;
    font-weight: bold;
    letter-spacing: 0.05em;
    line-height: 1.0;
  }
  .po-print-section {
    margin-bottom: 5mm;
  }
  .po-print-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6mm;
  }
  .po-print-grid-5 {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 3mm;
    border: 1px solid #000;
    padding: 2mm;
  }
  .po-print-grid-5 > div {
    border-right: 1px solid #ccc;
    padding-right: 3mm;
    font-size: 9pt;
  }
  .po-print-grid-5 > div:last-child { border-right: none; }
  .po-print-grid-5 .po-print-label {
    font-weight: bold;
    font-size: 8pt;
    text-transform: uppercase;
    color: #555;
  }
  .po-print-block-label {
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid #000;
    margin-bottom: 2mm;
    padding-bottom: 1mm;
  }
  .po-print-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 4mm;
  }
  .po-print-table th,
  .po-print-table td {
    border: 1px solid #000;
    padding: 2mm 3mm;
    text-align: left;
    font-size: 9.5pt;
  }
  .po-print-table th {
    background: #eee;
    font-weight: bold;
    text-transform: uppercase;
    font-size: 8.5pt;
  }
  .po-print-table td.num,
  .po-print-table th.num {
    text-align: right;
  }
  .po-print-totals {
    width: 70mm;
    margin-left: auto;
    margin-top: 3mm;
    border-collapse: collapse;
  }
  .po-print-totals td {
    padding: 1.5mm 3mm;
    font-size: 9.5pt;
  }
  .po-print-totals td:first-child {
    text-transform: uppercase;
    font-weight: bold;
    font-size: 8.5pt;
  }
  .po-print-totals td:last-child { text-align: right; }
  .po-print-totals tr.po-print-total-row td {
    border-top: 2px solid #000;
    font-size: 11pt;
    font-weight: bold;
  }
  .po-print-auth {
    margin-top: 8mm;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8mm;
  }
  .po-print-auth-line {
    border-bottom: 1px solid #000;
    margin-top: 10mm;
    padding-top: 1mm;
    font-size: 8pt;
    color: #555;
  }
  .po-print-footer {
    margin-top: 8mm;
    padding-top: 3mm;
    border-top: 1px solid #000;
    font-size: 8pt;
    color: #555;
    text-align: center;
  }
  .po-print-footer .po-print-controlled {
    font-weight: bold;
    text-transform: uppercase;
    color: #000;
    letter-spacing: 0.1em;
    font-size: 9pt;
    margin-bottom: 1mm;
  }
  /* Standard letter / A4 page break management */
  .po-print-table { page-break-inside: auto; }
  .po-print-table tr { page-break-inside: avoid; page-break-after: auto; }
  @page { size: A4 portrait; margin: 10mm; }

  /* AISC 9-category clause block (CR-2026-FE-PO-AISC-TERMS-BLOCK Option A) */
  .po-print-aisc-terms {
    margin-top: 6mm;
    border-top: 2px solid #000;
    padding-top: 4mm;
    page-break-inside: auto;
  }
  .po-print-aisc-terms-header {
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 11pt;
    margin-bottom: 2mm;
    text-align: center;
  }
  .po-print-aisc-terms-subhead {
    font-weight: bold;
    text-transform: uppercase;
    font-size: 9pt;
    margin-top: 3mm;
    margin-bottom: 1mm;
    border-bottom: 1px solid #999;
    padding-bottom: 0.5mm;
  }
  .po-print-aisc-terms-body {
    font-size: 8.5pt;
    line-height: 1.35;
    text-align: justify;
  }
  .po-print-aisc-terms-trace {
    margin-top: 4mm;
    font-size: 7.5pt;
    color: #555;
    border-top: 1px solid #ccc;
    padding-top: 2mm;
    text-align: center;
  }
}
`;

const Details = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState(null);
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPurchaseOrderData = async () => {
      try {
        const response = await ApiCall.get(`/purchaseOrder/${id}`);
        setFormData(response.data.data.purchaseOrder);
        setItems(response.data.data.PurchaseOrderItems);
      } catch (error) {
        console.error("An error occurred:", error);
      }
    };

    if (id) {
      fetchPurchaseOrderData();
    }
  }, [id]);

  const handleBackClick = () => {
    navigate("/PurchaseOrder");
  };

  const handlePrint = () => {
    window.print();
  };

  // Compute money breakdown for print layout. Tolerates fields being null /
  // missing / camelCase since current API returns snake_case but form may
  // evolve. Fallbacks default to 0 so the print layout never blanks out.
  const subtotal = formData
    ? Number(formData.subtotal) ||
      items.reduce((acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0)
    : 0;
  const taxRate = formData ? Number(formData.tax_rate) || 0 : 0;
  const salesTax = formData ? Number(formData.sales_tax) || subtotal * taxRate : 0;
  const shippingHandling = formData ? Number(formData.shipping_handling) || 0 : 0;
  const otherCharges = formData ? Number(formData.other_charges) || 0 : 0;
  const totalAmount = formData
    ? Number(formData.total_amount) || subtotal + salesTax + shippingHandling + otherCharges
    : 0;

  return (
    <div>
      <Helmet>
        <title>Purchase Order Details | SFM</title>
      </Helmet>
      {/* CR-2026-FE-PO-PRINT-LAYOUT — print-only stylesheet */}
      <style>{PRINT_CSS}</style>

      {/* SCREEN VIEW — unchanged from prior render path. Hidden on print. */}
      <div className="po-screen-view">
        <Container>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={5}
          >
            <Breadcrumbs aria-label="breadcrumb">
              <Stack direction="row" alignItems="center" spacing={1}>
                <HomeRounded color="inherit" />
                <Typography variant="body1" color="textPrimary">
                  / Purchase Order Details
                </Typography>
              </Stack>
            </Breadcrumbs>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PrintRounded />}
              onClick={handlePrint}
              disabled={!formData}
            >
              Print PO
            </Button>
          </Stack>
          <Typography variant="h4" style={{ marginBottom: "16px" }}>
            Purchase Order Details
          </Typography>
          <Paper>
            {formData && (
              <Card>
                {TABLE_HEAD.map((column) => (
                  <ListItem
                    key={column.id}
                    style={{ borderBottom: "1px solid #e0e0e0" }}
                  >
                    <Grid container style={{ marginBottom: "16px" }}>
                      <Grid item xs={6}>
                        <ListItemText primary={column.label} />
                      </Grid>
                      <Grid item xs={6}>
                        {column.id === "delivery_date" ||
                        column.id === "order_date"
                          ? fDate(formData[column.id])
                          : column.id === "phone"
                          ? formatPhoneNumber(formData[column.id])
                          : column.id === "fax"
                          ? formatfaxNumber(formData[column.id])
                          : formData[column.id]}
                      </Grid>
                    </Grid>
                  </ListItem>
                ))}
              </Card>
            )}
          </Paper>
          <Paper style={{ marginTop: "8px" }}>
            <Card>
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>Items</TableCell>
                      <TableCell>Quantity</TableCell>
                    </TableRow>
                  </TableBody>
                  {items.map((item) => (
                    <TableBody key={item.id}>
                      <TableRow>
                        <TableCell>{item.Inventory && item.Inventory.ediStdNomenclature}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                      </TableRow>
                    </TableBody>
                  ))}
                </Table>
              </TableContainer>
            </Card>
            <Stack direction="row" spacing={1} style={{ marginTop: "10px" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleBackClick}
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PrintRounded />}
                onClick={handlePrint}
                disabled={!formData}
              >
                Print PO
              </Button>
            </Stack>
          </Paper>
        </Container>
      </div>

      {/* PRINT VIEW — A4 cover sheet. Hidden on screen, visible on print only.
          Layout pattern based on D-034 §3.1 (PO Model 1) +
          §3.2 (generic). Currently emits the data block + Controlled Copy
          footer; the AISC 9-category clause block lands in
          CR-2026-FE-PO-AISC-TERMS-BLOCK (AP-class follow-up). */}
      {formData && (
        <div className="po-print-view" aria-hidden="true">
          <div className="po-print-header">
            <div>
              <div style={{ fontSize: "12pt", fontWeight: "bold" }}>
                {formData.company_name_text || "SFM Issuing Entity — set Company Name on PO"}
              </div>
              <div style={{ fontSize: "9pt", color: "#555", marginTop: "1mm" }}>
                {formData.address || ""}
              </div>
              <div style={{ fontSize: "9pt", color: "#555" }}>
                {formData.phone ? `Phone ${formatPhoneNumber(formData.phone) || formData.phone}` : ""}
                {formData.fax ? `   Fax ${formatfaxNumber(formData.fax) || formData.fax}` : ""}
              </div>
              <div style={{ fontSize: "9pt", color: "#555" }}>
                {formData.email || ""}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="po-print-title">PURCHASE</div>
              <div className="po-print-title">ORDER</div>
              <div style={{ marginTop: "3mm", fontSize: "10pt" }}>
                <strong>P.O. NUMBER:</strong> {formData.po_number || `#${formData.id || ""}`}
              </div>
              <div style={{ fontSize: "8pt", color: "#555", marginTop: "1mm" }}>
                The following number must appear on all related<br/>
                correspondence, shipping papers, and invoices.
              </div>
            </div>
          </div>

          {/* TO / SHIP TO blocks — D-034 §3.1 vendor-and-ship layout */}
          <div className="po-print-section po-print-grid-2">
            <div>
              <div className="po-print-block-label">To (Vendor)</div>
              <div>{formData.vendor_name_text || formData.vendor_name || ""}</div>
              <div>{formData.address || ""}</div>
              <div>{formData.phone ? `Phone ${formatPhoneNumber(formData.phone) || formData.phone}` : ""}</div>
              <div>{formData.email || ""}</div>
            </div>
            <div>
              <div className="po-print-block-label">Ship To</div>
              <div>{formData.ship_to || ""}</div>
            </div>
          </div>

          {/* PO Date / Requisitioner / Shipped Via / F.O.B. / Terms — D-034 §3.1 row 22 */}
          <div className="po-print-section po-print-grid-5">
            <div>
              <div className="po-print-label">P.O. Date</div>
              <div>{formData.order_date ? fDate(formData.order_date) : ""}</div>
            </div>
            <div>
              <div className="po-print-label">Requisitioner</div>
              <div>{formData.requisitioner || formData.order_by || ""}</div>
            </div>
            <div>
              <div className="po-print-label">Shipped Via</div>
              <div>{formData.ship_via || formData.placed_via || ""}</div>
            </div>
            <div>
              <div className="po-print-label">F.O.B. Point</div>
              <div>{formData.fob_point || ""}</div>
            </div>
            <div>
              <div className="po-print-label">Terms</div>
              <div>{formData.term || "Due on receipt"}</div>
            </div>
          </div>

          {/* Line items table */}
          <div className="po-print-section">
            <table className="po-print-table">
              <thead>
                <tr>
                  <th className="num" style={{ width: "12%" }}>Quantity</th>
                  <th style={{ width: "10%" }}>Unit</th>
                  <th>Description</th>
                  <th className="num" style={{ width: "16%" }}>Unit Price</th>
                  <th className="num" style={{ width: "16%" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {(items && items.length > 0
                  ? items
                  : Array.from({ length: 5 }).map((_, i) => ({ id: `blank-${i}` }))
                ).map((item) => {
                  const qty = Number(item.quantity) || 0;
                  const unitPrice = Number(item.unit_price) || 0;
                  const lineTotal = qty * unitPrice;
                  const desc =
                    (item.Inventory && item.Inventory.ediStdNomenclature) ||
                    item.description ||
                    "";
                  return (
                    <tr key={item.id}>
                      <td className="num">{item.quantity || ""}</td>
                      <td>{item.unit || ""}</td>
                      <td>{desc}</td>
                      <td className="num">{item.unit_price ? formatMoney(unitPrice) : ""}</td>
                      <td className="num">{lineTotal ? formatMoney(lineTotal) : ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <table className="po-print-totals">
              <tbody>
                <tr>
                  <td>Subtotal</td>
                  <td>{formatMoney(subtotal)}</td>
                </tr>
                <tr>
                  <td>Tax Rate</td>
                  <td>{taxRate ? `${(taxRate * 100).toFixed(2)}%` : ""}</td>
                </tr>
                <tr>
                  <td>Sales Tax</td>
                  <td>{formatMoney(salesTax)}</td>
                </tr>
                <tr>
                  <td>Shipping & Handling</td>
                  <td>{formatMoney(shippingHandling)}</td>
                </tr>
                <tr>
                  <td>Other</td>
                  <td>{formatMoney(otherCharges)}</td>
                </tr>
                <tr className="po-print-total-row">
                  <td>Total</td>
                  <td>{formatMoney(totalAmount)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {formData.notes ? (
            <div className="po-print-section">
              <div className="po-print-block-label">Notes</div>
              <div style={{ whiteSpace: "pre-wrap" }}>{formData.notes}</div>
            </div>
          ) : null}

          {/* Standard PO instructions block (matches D-034 §3.1 Authorization
              area). The 9-category AISC material-traceability clause block
              is INTENTIONALLY NOT INCLUDED here — that lands in
              CR-2026-FE-PO-AISC-TERMS-BLOCK driven by the planned
              po_terms_templates table. */}
          <div className="po-print-section" style={{ fontSize: "9pt" }}>
            <div>1. Please send two copies of your invoice.</div>
            <div>2. Enter this order in accordance with the prices, terms, delivery method, and specifications listed.</div>
            <div>3. Please notify us immediately if you are unable to ship as specified.</div>
          </div>

          {/* AISC 9-category clause block (CR-2026-FE-PO-AISC-TERMS-BLOCK Option A).
              Renders only when the PO appears to be structural-fab. A3-IP clause text
              per outputs/a3sfm-build/A3-PO-AISC-CLAUSE-BLOCK-DRAFT.md Rev 0 + D-034 §6
              (the STRUCTURAL CATEGORIES are A3 IP via pattern-extraction; verbatim
              source text is NOT used). */}
          {isStructuralFabPo(formData, items) && (
            <section className="po-print-aisc-terms">
              <div className="po-print-aisc-terms-header">
                Structural Fabrication Terms — AISC 207-25 / AISC 360 / AWS D1.1 / RCSC
              </div>

              <div className="po-print-aisc-terms-subhead">1. Material Identification and Marking</div>
              <div className="po-print-aisc-terms-body">
                All material supplied under this Purchase Order must be physically marked in a durable manner with: (a) the Purchase Order number printed at the top of this document; (b) the Piece Mark Number (PMK#) or Item Number from the line items table where applicable; (c) the size, grade, and ASTM specification under which the material is supplied; and (d) the Heat Number traceable to the Mill Test Report (MTR) for that lot. Marking shall conform to ASTM A6/A6M and to the marking requirements of AISC 303 (Code of Standard Practice for Steel Buildings and Bridges). For W-shape, channel, and HSS sections, marks shall be visible on the TOP flange and Web after stocking.
              </div>

              <div className="po-print-aisc-terms-subhead">2. Material Grades and Mill Test Reports</div>
              <div className="po-print-aisc-terms-body">
                Unless otherwise specified on the line items table, the following grades apply by section type: W-shapes / channels / S-shapes / M-shapes &mdash; ASTM A992 or A572 Grade 50 (Fy = 50 ksi); HSS round / square / rectangular &mdash; ASTM A500 Grade B or Grade C; plate / bar / pipe &mdash; ASTM A36, A992, or A572 as designated, Grade 50 default for plate &ge; 3/8&quot;; high-strength bolts / nuts / washers &mdash; ASTM A325 or A490, tested per RCSC latest edition. <strong>A Mill Test Report (MTR) traceable to the heat lot of each delivered item must accompany or precede delivery and must reference this Purchase Order number AND the corresponding heat number. Material delivered without a corresponding MTR may be refused at A3&apos;s sole discretion. Invoices for material lacking MTRs will be held in abeyance until MTRs are provided.</strong>
              </div>

              <div className="po-print-aisc-terms-subhead">3. Paint and Surface Coatings</div>
              <div className="po-print-aisc-terms-body">
                All paint, primer, intermediate-coat, and topcoat material supplied shall be accompanied by: (a) the manufacturer&apos;s Technical Data Sheet (TDS) for each lot; (b) a Certificate of Conformance (CoC) certifying conformance to the specification stated on the line items table, including dry-film-thickness range, VOC compliance, and recoat window; and (c) where SSPC-PS, AWS, or AISC 207-25 §1.10 / CCE supplement requirements apply, a Certificate of Analysis (CoA) showing the lot&apos;s conformance to the qualified-product list. Material delivered without TDS + CoC may be refused.
              </div>

              <div className="po-print-aisc-terms-subhead">4. Welding Consumables</div>
              <div className="po-print-aisc-terms-body">
                All welding consumables (electrodes, filler metals, fluxes, shielding gases) shall: (a) be classified as Low-Hydrogen designation per AWS A5.x where the line items table does not specify otherwise; (b) be supplied with a Certificate of Conformance (CoC) certifying classification, lot number, and conformance to AWS A5.x for the listed product; (c) be supplied in sealed, weather-protected packaging with the manufacturer&apos;s label intact. Conformance to AWS D1.1 §5.3 and AISC 207-25 §2.5 required. Material delivered without CoC may be refused.
              </div>

              <div className="po-print-aisc-terms-subhead">5. Material Protection in Transit</div>
              <div className="po-print-aisc-terms-body">
                Material delivered between November 10 and May 1 of each year, or at any time when forecast precipitation exceeds 0.10 inch within 24 hours of expected delivery, shall be tarped for the length of the trailer. Improperly tarped or untarped material that arrives in a state requiring restoration (rust bloom, water staining, blast-coat damage) may incur a service charge for restoration billed at A3&apos;s then-current rate, or at A3&apos;s election, the material may be refused.
              </div>

              <div className="po-print-aisc-terms-subhead">6. Bill of Lading and Mill-Certification Linkage</div>
              <div className="po-print-aisc-terms-body">
                Each delivery shall be accompanied by a signed, dated Bill of Lading (BOL). The BOL shall: (a) reference this Purchase Order number; (b) attach or reference all applicable Mill Test Reports for items shipped; (c) link each MTR to the specific heat number and line-item piece-mark number on this Purchase Order. MTRs may be transmitted electronically in advance, in which case the BOL shall reference the transmission ID and date. <strong>Invoices submitted against deliveries lacking BOL-MTR linkage will not be paid until the linkage is established.</strong> Supports A3&apos;s responsibilities under AISC 207-25 §2.6 + §2.6.4 and ISO 9001:2015 §7.5.3.
              </div>

              <div className="po-print-aisc-terms-subhead">7. Receiving and Physical Handling</div>
              <div className="po-print-aisc-terms-body">
                Standard receiving hours coordinated with the Requisitioner. Material shall be loaded forklift-friendly with appropriate dunnage. Side-to-side max spacing on plate/stock not exceeding 3&apos;-6&quot;; max unit lift weight side-unload-with-forklift not exceeding 5 tons; bundles not exceeding 5,000 lbs; plate shipped with min 3&quot; square wooden block. Drivers shall report to the receiving location designated on this Purchase Order&apos;s &quot;Ship To&quot; block; Drivers shall not contact the Requisitioner directly.
              </div>

              <div className="po-print-aisc-terms-subhead">8. Supplier Review and Response Obligations</div>
              <div className="po-print-aisc-terms-body">
                Upon receipt of this Purchase Order, the Supplier shall review the order for back-order or lead-time conflicts, material certification or quality issues, resource-loading constraints, and specification ambiguities. <strong>The Supplier shall notify A3 in writing of any such issue no later than two (2) business days from receipt.</strong> Absent such notification, all listed material shall be deemed available and shall ship on or before the requested delivery date. Where shipping in advance, Supplier shall provide A3 not less than one (1) business day advance notification. Single-PO-per-trailer preferred; on consolidated loads, only one PO may be partial; Supplier shall color-code and bundle parts by PO. Each PO invoiced separately; mixed-PO invoicing not accepted.
              </div>

              <div className="po-print-aisc-terms-subhead">9. High-Strength Fastener Procurement</div>
              <div className="po-print-aisc-terms-body">
                All bolts, nuts, washers, and direct-tension indicators shall be of North American origin (raw material, manufacture, and assembly all in North America). Fastener assemblies shall be supplied pre-assembled unless line items specify otherwise. Galvanized fasteners shall be mechanical-zinc galvanized (default); hot-dip galvanized only on a per-order basis with prior written approval; no black-colored thread lubricant. A325 / A490 fasteners shall conform to RCSC &quot;Specification for Structural Joints Using High-Strength Bolts&quot; latest edition. Fastener assemblies in sealed containers with weather-proof labels (top + side) listing PO number, fastener size and grade, container quantity, and Mill Test Report Lot Number. MTRs transmitted by email as <code>{`{PO-number}-mtr.pdf`}</code>. <strong>Evidence of Rotational Capacity testing per RCSC §11 shall be provided with the MTRs.</strong>
              </div>

              <div className="po-print-aisc-terms-subhead">10. Ordering Information (ASTM A6/A6M §4)</div>
              <div className="po-print-aisc-terms-body">
                ASTM A6/A6M provides the general requirements only; material is ordered to a structural grade (A36, A572 Gr.&nbsp;50, A992, A709) with A6/A6M supplying the general requirements unless the product specification states otherwise. Each structural-steel line item on this Purchase Order shall specify, per ASTM A6/A6M §4: (a) quantity &mdash; number of pieces and/or total weight; (b) product form &mdash; shape (W, M, S, HP, C, MC, L), plate, bar, or sheet piling; (c) the applicable ASTM product specification, grade, AND edition year (e.g., ASTM A992/A992M Gr.&nbsp;50, current edition); (d) size / designation and dimensions &mdash; shapes by designation (e.g., W12&times;26, which fixes nominal depth and weight per foot), plate and bar by thickness &times; width &times; length; (e) length &mdash; specific cut length or random; (f) unit system &mdash; A6 (inch-pound) or A6M (SI), stated on the order (the A6M designation makes SI tolerances mandatory); and (g) condition (as-rolled, cut-to-length, etc.). Dimensional, flatness, camber, and sweep tolerances per ASTM A6/A6M apply unless otherwise specified. Special and Supplementary requirements (Charpy V-notch impact testing and temperature, fine-grain practice, maximum carbon equivalent) apply only where specified on the line items table.
              </div>

              <div className="po-print-aisc-terms-subhead">Reject Notice Procedure</div>
              <div className="po-print-aisc-terms-body">
                Where A3 rejects material, A3 shall issue a written Reject Notice specifying PO number, item description and quantity, and reason for rejection. Supplier shall: (a) acknowledge within one business day; (b) issue a signed pickup notice prior to driver dispatch; (c) ensure driver carries pickup notice with receiving-office countersignature before loading; (d) collect rejected material within seven calendar days. Material not collected within seven days may be disposed of at A3&apos;s election with no further obligation owed to Supplier.
              </div>

              <div className="po-print-aisc-terms-trace">
                Coverage: AISC 207-25 §1.5.0 Order Review · §2.5 Welding · §2.6 Material Identification · §2.6.4 Receiving Inspection · CCE §6 Coatings ·
                AISC 360 §A3.1 Material Specifications · §J3 High-Strength Bolts ·
                AISC 303 (Code of Standard Practice) ·
                AWS D1.1 §5.3 Welding Consumables ·
                RCSC §2 + §11 ·
                ASTM A6/A6M, A992, A572, A500, A36, A325, A490, F3125 ·
                ISO 9001:2015 §7.5.3 + §8.4.2 + §8.4.3 + §8.5.4 + §8.7
              </div>
            </section>
          )}

          <div className="po-print-auth">
            <div>
              <div className="po-print-block-label">Send Correspondence To</div>
              <div>{formData.confirm_with || formData.order_by || ""}</div>
              <div>{formData.address || ""}</div>
              <div>
                {formData.phone ? `Phone ${formatPhoneNumber(formData.phone) || formData.phone}` : ""}
                {formData.fax ? `   Fax ${formatfaxNumber(formData.fax) || formData.fax}` : ""}
              </div>
            </div>
            <div>
              <div className="po-print-block-label">Authorization</div>
              <div className="po-print-auth-line">Authorized by</div>
              <div className="po-print-auth-line">Date</div>
            </div>
          </div>

          <div className="po-print-footer">
            <div className="po-print-controlled">Controlled Copy — Do Not Duplicate</div>
            <div>
              Layout per D-034 §3.1 (A3 Reference Forms — Purchase Order Templates) ·
              SFM PO #{formData.id || ""} ·
              Generated {new Date().toLocaleDateString("en-US")}
            </div>
            <div style={{ marginTop: "1mm" }}>
              AISC material-traceability clause block (10 categories, incl. ASTM A6/A6M §4 ordering information) per
              CR-2026-FE-PO-AISC-TERMS-BLOCK. This print layout closes
              CR-2026-FE-PO-PRINT-LAYOUT (Class P).
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Details;
